/**
 * Lista de recogidas con filtros y acciones CRUD
 *
 * Control de acceso: los roles se resuelven en src/routes.js (AuthRoute), NO
 * aquí. Envolver además la página en RequierePermisos duplicaba la protección
 * y, al tapar el render con su propio spinner, retrasaba el montaje de la Card:
 * la carga se veía a nivel de página en vez de dentro del DataGrid.
 */
import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import {
    Box,
    Card,
    Typography,
    Button,
    useTheme,
    useMediaQuery,
    Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BlockIcon from "@mui/icons-material/Block";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";

import RecogidasService from "../api/recogidas.api";
import Toasts from "../../../components/notificacion/Toasts";
import { StateContext } from "../../../components/context/StateProvider";
import { RECOGIDA_ESTADO } from "../constants";
import { generateRecogidaColumns } from "../helpers/columns";

import {
    FiltrosRecogidas,
    ModalEliminar,
    ModalConfirmarAccion,
    ModalSeguimiento,
    RecogidasMobileList,
} from "../components";
import { aplicarFiltrosCliente } from "../helpers/filtros";
import {
    DATA_GRID_SX,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_INITIAL_STATE,
    DATA_GRID_PAGE_SIZE_OPTIONS,
} from "../../../components/ui/dataGridStyles";
import { BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";



// Valores iniciales de fechas para los filtros (últimos 30 días)
const getFechaInicioInicial = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
};
const getFechaFinInicial = () => new Date().toISOString().slice(0, 10);

export default function ListaRecogidas() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const { state, updateState } = useContext(StateContext);

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);

    // Estados de inputs de filtros
    const [fechaInicio, setFechaInicio] = useState(getFechaInicioInicial);
    const [fechaFin, setFechaFin] = useState(getFechaFinInicial);
    const [estado, setEstado] = useState("");
    const [q, setQ] = useState("");

    // Filtros aplicados (cliente-side)
    const [filtrosAplicados, setFiltrosAplicados] = useState({
        fechaInicio: getFechaInicioInicial(),
        fechaFin: getFechaFinInicial(),
        estado: "",
        q: ""
    });

    // Modal eliminar
    const [accionPendiente, setAccionPendiente] = useState(null);
    const [ejecutandoAccion, setEjecutandoAccion] = useState(false);
    const [showModalEliminar, setShowModalEliminar] = useState(false);
    const [recogidaAEliminar, setRecogidaAEliminar] = useState(null);

    // Modal seguimiento
    const [recogidaSeguimiento, setRecogidaSeguimiento] = useState(null);
    const [sincronizando, setSincronizando] = useState(false);

    useEffect(() => {
        cargarRecogidas();
    }, []);

    const cargarRecogidas = async () => {
        try {
            const data = await RecogidasService.list({
                desde: fechaInicio,
                hasta: fechaFin,
                estado: "",
            });
            setRows(data ?? []);
        } catch (error) {
            const toast = Toasts.generateToast(error.message, 'error');
            updateState({ toasts: [...state.toasts, toast] });
        } finally {
            setLoading(false);
        }
    };

    const handleFiltrar = async () => {
        setLoading(true);
        try {
            const data = await RecogidasService.list({
                desde: fechaInicio,
                hasta: fechaFin,
                estado,
            });

            setRows(data ?? []);
            setFiltrosAplicados({ fechaInicio, fechaFin, estado, q });
        } catch (err) {
            console.error('Error al aplicar filtros:', err);
            updateState({
                toasts: [...state.toasts, Toasts.generateToast("Error al filtrar recogidas.", "error")],
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLimpiar = () => {
        setFechaInicio(getFechaInicioInicial());
        setFechaFin(getFechaFinInicial());
        setEstado("");
        setQ("");
        setFiltrosAplicados({
            fechaInicio: getFechaInicioInicial(),
            fechaFin: getFechaFinInicial(),
            estado: "",
            q: ""
        });
    };

    // Filas filtradas en cliente. El estado ya lo filtra la API, pero se vuelve a aplicar
    // aquí para no depender de que lo respete; la búsqueda de texto sí es solo de cliente
    const rowsView = useMemo(() => {
        let filtradas = [...rows];

        if (filtrosAplicados.estado) {
            filtradas = filtradas.filter(r => r.estado === filtrosAplicados.estado);
        }

        return aplicarFiltrosCliente(filtradas, { q: filtrosAplicados.q });
    }, [rows, filtrosAplicados]);

    // Handlers
    const handleNueva = () => {
        navigate("/recogidas/nueva-recogida");
    };

    const handleEliminar = useCallback((recogida) => {
        // Solo permitir eliminar si está solicitada o en error (no confirmada)
        if (recogida.estado !== RECOGIDA_ESTADO.SOLICITADA && recogida.estado !== RECOGIDA_ESTADO.ERROR) {
            updateState({
                toasts: [...state.toasts, Toasts.generateToast("Solo se pueden eliminar recogidas en estado Solicitada o Error.", "warning")],
            });
            return;
        }
        setRecogidaAEliminar(recogida);
        setShowModalEliminar(true);
    }, [state, updateState]);

    const handleVerSeguimiento = useCallback((recogida) => {
        setRecogidaSeguimiento(recogida);
    }, []);

    const handleCancelar = useCallback((recogida) => {
        setAccionPendiente({ tipo: 'cancelar', recogida });
    }, []);

    const handleRecepcionar = useCallback((recogida) => {
        setAccionPendiente({ tipo: 'recepcionar', recogida });
    }, []);

    /**
     * Ejecuta la acción de estado confirmada en el modal.
     *
     * Se recarga el listado en vez de actualizar la fila en memoria: CANCELADA y
     * RECEPCIONADA quedan fuera del listado por defecto, así que la fila debe
     * desaparecer igual que la ve el backend.
     */
    const confirmarAccion = async () => {
        if (!accionPendiente) return;

        const { tipo, recogida } = accionPendiente;
        const esCancelar = tipo === 'cancelar';

        setEjecutandoAccion(true);

        try {
            if (esCancelar) {
                await RecogidasService.cancelar(recogida.id);
            } else {
                await RecogidasService.recepcionar(recogida.id);
            }

            updateState({
                toasts: [...state.toasts, Toasts.generateToast(
                    esCancelar ? "Recogida cancelada." : "Recogida marcada como recibida en almacén.",
                    "success"
                )],
            });

            setAccionPendiente(null);
            await cargarRecogidas();
        } catch (err) {
            updateState({
                toasts: [...state.toasts, Toasts.generateToast(err.message || "No se pudo completar la acción.", "error")],
            });
        } finally {
            setEjecutandoAccion(false);
        }
    };

    /**
     * Fuerza la sincronización con MSoft de la recogida abierta en el modal.
     * La API devuelve la recogida actualizada, así que se refresca esa fila en memoria
     * y el propio modal, sin recargar el listado completo.
     */
    const handleSincronizar = async () => {
        if (!recogidaSeguimiento) return;

        setSincronizando(true);

        try {
            const actualizada = await RecogidasService.sincronizar(recogidaSeguimiento.id);

            setRows((anteriores) => anteriores.map((r) => (r.id === actualizada.id ? actualizada : r)));
            setRecogidaSeguimiento(actualizada);

            updateState({
                toasts: [...state.toasts, Toasts.generateToast("Seguimiento sincronizado con MSoft.", "success")],
            });
        } catch (err) {
            updateState({
                toasts: [...state.toasts, Toasts.generateToast(err.message || "Error al sincronizar.", "error")],
            });
        } finally {
            setSincronizando(false);
        }
    };

    const confirmarEliminar = async () => {
        if (!recogidaAEliminar) return;

        try {
            setLoading(true);
            await RecogidasService.delete(recogidaAEliminar.id);

            updateState({
                toasts: [...state.toasts, Toasts.generateToast("Recogida eliminada correctamente.", "success")],
            });

            setShowModalEliminar(false);
            setRecogidaAEliminar(null);
            await cargarRecogidas();
        } catch (err) {
            console.error('Error al eliminar recogida:', err);
            updateState({
                toasts: [...state.toasts, Toasts.generateToast(err.message || "Error al eliminar recogida.", "error")],
            });
        } finally {
            setLoading(false);
        }
    };

    const cancelarEliminar = () => {
        setShowModalEliminar(false);
        setRecogidaAEliminar(null);
    };

    // Columnas del DataGrid
    const columns = useMemo(
        () => generateRecogidaColumns({ onCancelar: handleCancelar, onRecepcionar: handleRecepcionar, onEliminar: handleEliminar, onVerSeguimiento: handleVerSeguimiento }),
        [handleCancelar, handleRecepcionar, handleEliminar, handleVerSeguimiento]
    );

    return (
        <Box id="lista-recogidas" sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "stretch", sm: "flex-start" }, gap: 2, mb: 2 }}>
                        <Box>
                            <Typography
                                variant={isMobile ? "h6" : "h5"}
                                sx={{
                                    fontWeight: 700,
                                    color: "#212529",
                                    mb: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <LocalShippingIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                                Recogidas
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#6c757d" }}>
                                Consulta y gestiona las recogidas de mercancía
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleNueva}
                            fullWidth={isMobile}
                            sx={BOTON_PRIMARIO_SX}
                        >
                            Nueva recogida
                        </Button>
                    </Box>


                    {/* Filtros: siempre visibles. Si se ocultan cuando no hay resultados,
                        un filtro que no devuelve nada deja al usuario sin forma de cambiarlo */}
                    <Divider sx={{ my: 2 }} />
                    <FiltrosRecogidas
                        fechaInicio={fechaInicio}
                        setFechaInicio={setFechaInicio}
                        fechaFin={fechaFin}
                        setFechaFin={setFechaFin}
                        estado={estado}
                        setEstado={setEstado}
                        q={q}
                        setQ={setQ}
                        onFiltrar={handleFiltrar}
                        onLimpiar={handleLimpiar}
                        loading={loading}
                        totalResultados={rowsView.length}
                    />
                </Box>

                {/* Contenido */}
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {!isMobile ? (
                        <DataGrid
                            rows={rowsView}
                            columns={columns}
                            localeText={DATA_GRID_LOCALE_TEXT}
                            autoHeight
                            loading={loading}
                            initialState={DATA_GRID_INITIAL_STATE}
                            pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                            disableRowSelectionOnClick
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={DATA_GRID_SX}
                        />
                    ) : (
                        <RecogidasMobileList
                            rows={rowsView}
                            loading={loading}
                            onCancelar={handleCancelar}
                            onRecepcionar={handleRecepcionar}
                            onEliminar={handleEliminar}
                            onVerSeguimiento={handleVerSeguimiento}
                        />
                    )}
                </Box>
            </Card>

            {/* Modal Eliminar */}
            <ModalEliminar
                open={showModalEliminar}
                recogida={recogidaAEliminar}
                onConfirm={confirmarEliminar}
                onCancel={cancelarEliminar}
            />

            {/* Modal Cancelar / Recibir en almacén */}
            <ModalConfirmarAccion
                open={Boolean(accionPendiente)}
                recogida={accionPendiente?.recogida}
                titulo={accionPendiente?.tipo === 'cancelar' ? "Cancelar recogida" : "Recibida en almacén"}
                tituloMobile={accionPendiente?.tipo === 'cancelar' ? "Cancelar" : "Recibida"}
                mensaje={accionPendiente?.tipo === 'cancelar'
                    ? "La recogida se cancelará y saldrá del ciclo de sincronización."
                    : "Se dará por recibida la mercancía en el almacén y se cerrará el ciclo de la recogida."}
                aviso={accionPendiente?.tipo === 'cancelar'
                    ? "No se da de baja en MSoft. Si la recogida sigue viva allí, hay que anularla también."
                    : undefined}
                confirmLabel={accionPendiente?.tipo === 'cancelar' ? "Cancelar recogida" : "Marcar como recibida"}
                confirmColor={accionPendiente?.tipo === 'cancelar' ? "#cc071e" : "#196f3d"}
                icon={accionPendiente?.tipo === 'cancelar' ? BlockIcon : WarehouseIcon}
                onConfirm={confirmarAccion}
                onCancel={() => setAccionPendiente(null)}
                loading={ejecutandoAccion}
            />

            {/* Modal Seguimiento */}
            <ModalSeguimiento
                show={Boolean(recogidaSeguimiento)}
                recogida={recogidaSeguimiento}
                onHide={() => setRecogidaSeguimiento(null)}
                onSincronizar={handleSincronizar}
                sincronizando={sincronizando}
            />
        </Box>
    );
}
